import { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';

export type LinkVariant = 'brand' | 'quiet';

const variants: Record<LinkVariant, string> = {
  brand: 'text-brand-text hover:underline',
  quiet: 'text-fg-secondary hover:text-fg hover:underline',
};

export interface LinkProps extends Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'className'
> {
  variant?: LinkVariant;
  /** Opens in a new tab with an out-arrow affordance + safe rel. */
  external?: boolean;
  className?: string;
  children: ReactNode;
}

const linkClass = (variant: LinkVariant, className?: string) =>
  cx(
    'inline-flex items-center gap-0.5 font-medium outline-none',
    variants[variant],
    className,
  );

/** Bare anchor — external links, or anything rendered outside a Router. */
const PlainLink = ({
  variant = 'brand',
  external,
  className,
  children,
  ...props
}: LinkProps) => (
  <a
    {...props}
    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    className={linkClass(variant, className)}
  >
    {children}
    {external && <Icon as={ArrowUpRight} size="xs" />}
  </a>
);

/**
 * Internal anchor that navigates client-side via the router, so following it
 * doesn't reload the whole SPA. Keeps a real `href` (right-click / open-in-new
 * tab / middle-click still work); only a plain left-click is intercepted.
 */
const ClientLink = ({
  variant = 'brand',
  className,
  children,
  onClick,
  href,
  ...props
}: LinkProps) => {
  const navigate = useNavigate();
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    // Clicking a link means "go where the link points", so don't let an
    // ancestor click handler (e.g. a DataTable row that navigates elsewhere)
    // also fire and win the navigation race.
    e.stopPropagation();
    onClick?.(e);
    if (
      !e.defaultPrevented &&
      e.button === 0 &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.shiftKey &&
      !e.altKey
    ) {
      e.preventDefault();
      navigate(href as string);
    }
  };
  return (
    <a
      {...props}
      href={href}
      onClick={handleClick}
      className={linkClass(variant, className)}
    >
      {children}
    </a>
  );
};

/**
 * Text link — foundation brand color, underline on hover. `external` opens a
 * new tab with an out-arrow. Internal links (`href` starting with `/`) navigate
 * client-side when inside a router; everything else is a plain anchor. Keeps
 * link styling in one place (replaces ad-hoc `<a className="text-brand-text">`).
 */
export const Link = (props: LinkProps) => {
  const inRouter = useInRouterContext();
  const internal =
    !props.external &&
    typeof props.href === 'string' &&
    props.href.startsWith('/');
  return inRouter && internal ? (
    <ClientLink {...props} />
  ) : (
    <PlainLink {...props} />
  );
};
